import { Box, Button, Stack } from '@mui/material'
import React, { useContext, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useBoolean } from 'react-use'

import { StyledRouterLink } from 'components/common/StyledRouterLink'
import {
  AuthenticationFormValues,
  AWSAuthenticationType,
  isSelectedObservabilityToolAuthenticated
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAuthenticateAndSaveButton } from 'components/status-checks/common/ObservabilityToolAuthenticateAndSaveButton'
import { ObservabilityToolAuthenticationStatus } from 'components/status-checks/common/ObservabilityToolAuthenticationStatus'
import { ObservabilityToolAndDomainSelect } from 'components/status-checks/form/authentication/ObservabilityToolAndDomainSelect'
import { ObservabilityToolAuthenticationDetails } from 'components/status-checks/form/authentication/ObservabilityToolAuthenticationDetails'
import {
  buildTeamExternalIntegration,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import {
  ObservabilityToolType,
  TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus
} from 'types/api/status-checks/status-check'

export const ObservabilityToolAuthentication: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  handleCancel?: () => void
  handleNext?(): void
  isAuthenticationFormValid: boolean
  isAuthenticationSaving: boolean
  setIsAuthenticationSaving(arg: boolean): void
}> = ({
  externalIntegrations,
  handleCancel,
  handleNext = () => {},
  isAuthenticationFormValid,
  isAuthenticationSaving,
  setIsAuthenticationSaving
}) => {
  const { trans } = useTranslation()

  const [isAuthenticationValid, setIsAuthenticationValid] = useBoolean(false)

  const { control, formState, getValues, setValue, trigger } = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()
  const { isEdit, isDrawerFlow } = useContext(
    ObservabilityToolAuthenticationContext
  )

  // Watches selected form values and re-renders when changed
  const [
    selectedObservabilityTool,
    externalIntegration,
    awsAuthenticationType,
    awsIamRoleArn,
    awsAccessKeyID,
    awsSecretAccessKey
  ] = useWatch<StatusCheckFormValues | AuthenticationFormValues>({
    control,
    name: [
      'observabilityTool',
      'externalIntegration',
      'awsAuthenticationType',
      'awsIamRoleArn',
      'awsAccessKeyID',
      'awsSecretAccessKey'
    ]
  })

  const isObservabilityToolAuthenticated = useMemo(() => {
    return isSelectedObservabilityToolAuthenticated(
      selectedObservabilityTool,
      externalIntegrations
    )
  }, [externalIntegrations, selectedObservabilityTool])
  const statusChecksUrls = getStatusChecksUrls()

  const isObservabilityToolSelected = Boolean(selectedObservabilityTool?.name)
  const isCustomObservabilityTool =
    selectedObservabilityTool?.type === ObservabilityToolType.CUSTOM
  const isAWS = selectedObservabilityTool?.type === ObservabilityToolType.AWS

  const handleTestTeamExternalIntegrationError = () => {
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.UnAuthenticated
    )
  }

  const handleTestTeamExternalIntegrationSuccess = async () => {
    await trigger('observabilityToolNickname')
    setIsAuthenticationValid(true)
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.Authenticated
    )
  }

  const shouldShowAuthenticationConfiguration = useMemo(() => {
    if (isEdit) {
      return true
    }

    // Since using an AWS authentication is a multiple-step process, we always show the authentication configuration when
    // AWS is selected
    if (isAWS) {
      return true
    }

    return !isObservabilityToolAuthenticated && isObservabilityToolSelected
  }, [
    isAWS,
    isEdit,
    isObservabilityToolAuthenticated,
    isObservabilityToolSelected
  ])

  const observabilityToolTypeForAuthentication = useMemo(() => {
    if (isAWS) {
      return ObservabilityToolType.AWS
    }
    if (isCustomObservabilityTool) {
      return ObservabilityToolType.CUSTOM
    }
    return 'other'
  }, [isAWS, isCustomObservabilityTool])

  const hasAWSErrors = useMemo(() => {
    // We build the integration here to ensure we have the latest form values
    const integration = buildTeamExternalIntegration(getValues())

    if (awsAuthenticationType === AWSAuthenticationType.ServiceAccount) {
      return (
        Boolean(formState.errors.authenticationUrl) ||
        Boolean(formState.errors.awsAccessKeyID) ||
        Boolean(formState.errors.awsSecretAccessKey) ||
        Boolean(formState.errors.domain) ||
        !awsAccessKeyID ||
        !awsSecretAccessKey ||
        !integration.integrationSpecificValues?.AWS_ACCESS_KEY_ID ||
        !integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
      )
    }

    return (
      !awsIamRoleArn ||
      Boolean(formState.errors.awsIamRoleArn) ||
      !integration.integrationSpecificValues?.AWS_IAM_ROLE_ARN
    )
  }, [
    getValues,
    awsAuthenticationType,
    awsIamRoleArn,
    formState.errors.awsIamRoleArn,
    formState.errors.authenticationUrl,
    formState.errors.awsAccessKeyID,
    formState.errors.awsSecretAccessKey,
    formState.errors.domain,
    awsAccessKeyID,
    awsSecretAccessKey
  ])

  // Determines if the "Authenticate" step passes sub-schema validation
  const isAuthenticateStepValid = useMemo(() => {
    return (
      isAuthenticationFormValid &&
      externalIntegration?.lastAuthenticationStatus ===
        TeamExternalIntegrationAuthenticationStatus.Authenticated &&
      externalIntegration?.isConfigured
    )
  }, [
    externalIntegration?.isConfigured,
    externalIntegration?.lastAuthenticationStatus,
    isAuthenticationFormValid
  ])

  const isTestTeamExternalIntegrationButtonDisabled = useMemo(() => {
    if (isEdit) {
      if (isAWS) {
        return hasAWSErrors || !formState.isDirty
      }

      return (
        !formState.isValid ||
        !formState.isDirty ||
        Boolean(formState.errors.headers) ||
        Boolean(formState.errors.observabilityToolNickname) ||
        externalIntegration?.lastAuthenticationStatus !==
          TeamExternalIntegrationAuthenticationStatus.Authenticated
      )
    }

    if (isAWS) {
      return hasAWSErrors
    }

    if (isAuthenticationValid) {
      return isAuthenticateStepValid
    }

    return Boolean(formState.errors.headers)
  }, [
    externalIntegration?.lastAuthenticationStatus,
    formState.errors.headers,
    formState.errors.observabilityToolNickname,
    formState.isDirty,
    formState.isValid,
    hasAWSErrors,
    isAWS,
    isAuthenticateStepValid,
    isAuthenticationValid,
    isEdit
  ])

  const isAuthenticated = useMemo(() => {
    return isSelectedObservabilityToolAuthenticated(
      selectedObservabilityTool,
      externalIntegrations
    )
  }, [externalIntegrations, selectedObservabilityTool])

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        {/*hide select in drawer flow since we want to the auth type to only one option there*/}
        {!isDrawerFlow && (
          <ObservabilityToolAndDomainSelect
            externalIntegrations={externalIntegrations}
            isEdit={isEdit}
            selectedObservabilityTool={selectedObservabilityTool}
          />
        )}
        {selectedObservabilityTool.name && (
          <Stack spacing={2}>
            {shouldShowAuthenticationConfiguration && (
              <ObservabilityToolAuthenticationDetails
                isAuthenticationValid={isAuthenticationValid}
                observabilityToolType={observabilityToolTypeForAuthentication}
                setIsAuthenticationValid={setIsAuthenticationValid}
              />
            )}
            <Box>
              <Stack alignItems="baseline" direction="row" mt={2} spacing={2}>
                {(!isAuthenticated || isEdit) && (
                  <ObservabilityToolAuthenticateAndSaveButton
                    disabled={isTestTeamExternalIntegrationButtonDisabled}
                    externalIntegrations={externalIntegrations}
                    isAuthenticationSaving={isAuthenticationSaving}
                    setIsAuthenticationSaving={setIsAuthenticationSaving}
                    onTestError={handleTestTeamExternalIntegrationError}
                    onTestSuccess={handleTestTeamExternalIntegrationSuccess}
                  />
                )}
                {isAuthenticated && !isEdit && (
                  <Button
                    data-testid="observability-tool-authentication-next-button"
                    variant="contained"
                    onClick={handleNext}
                  >
                    {trans('next')}
                  </Button>
                )}
                {isEdit && !isAuthenticationSaving && !isDrawerFlow && (
                  <Button
                    replace
                    color="secondary"
                    component={StyledRouterLink}
                    sx={{ height: 'fit-content', width: 'fit-content' }}
                    to={statusChecksUrls.authentication()}
                    variant="contained"
                  >
                    {trans('cancel')}
                  </Button>
                )}
                {isEdit && !isAuthenticationSaving && isDrawerFlow && (
                  <Button
                    color="secondary"
                    sx={{ height: 'fit-content', width: 'fit-content' }}
                    variant="contained"
                    onClick={handleCancel}
                  >
                    {trans('cancel')}
                  </Button>
                )}
              </Stack>
            </Box>
            <ObservabilityToolAuthenticationStatus
              isLoading={isAuthenticationSaving}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
